import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, getFormValues, getFormAsyncErrors } from "redux-form";
import { Modal, Button } from "rhombus-react";
import { isEmpty } from "lodash";
import { pluralize } from "utils/common";

import { AI_GENERATE_VERSIONS_FORM } from "./constants";
import AssetsPanel from "./AssetsPanel";
import GeneratedPreviews from "./GeneratedPreviews";
import { getAssetRefinements } from "reducers/Refinements";
import { getVersionGenSelectedPreview } from "utils/Flags";


function requireSelectedVersions(value){
	const anySelected = Object.values(value).some(creative => creative.variations?.length);
	if(!anySelected){
		return "At least one version must be selected for creation.";
	}
}

export default function GeneratedVersionsBlade(props){
	const {
		onClose,
		onBack,
		libraryId,
		valid,
		submitting,
		handleSubmit,
	} = props;

	const isAIDevEnabled = getVersionGenSelectedPreview()
		, devClass = isAIDevEnabled ? "ai-dev-mode" : ""
		;

	const [showSelected, setShowSelected] = useState(false);

	const {
		creatives,
		isGeneratingAssets,
		generatedAssets,
		variations,
	} = useSelector(state => state.versionGeneration);

	const refinementsState = useSelector(state => state.refinements);
	const assetRefinements = refinementsState?.assetRefinements || {};

	const { selectedVariations = {} } = useSelector(getFormValues(AI_GENERATE_VERSIONS_FORM));
	const numSelected = Object.values(selectedVariations).reduce((prev, { variations=[]}) => {
		return prev + variations.length;
	}, 0);

	const asyncErrors = useSelector(getFormAsyncErrors(AI_GENERATE_VERSIONS_FORM));
	const dispatch = useDispatch();


	useEffect(() => {
		if (isEmpty(assetRefinements)
			&& !isGeneratingAssets
			&& generatedAssets?.images?.[0]?.values?.length) {
			const assets = generatedAssets.images[0].values.map(image => ({ src: image.src }));
			const dimensionInfos = creatives.reduce((acc, { width, height }) => {
				const name = `${width}x${height}`;
				if (!acc.names.has(name)) {
					acc.dimensions.push({ name, width, height });
					acc.names.add(name);
				}
				return acc;
			}, { dimensions: [], names: new Set() });
			const data = { assets, dimensions: dimensionInfos.dimensions };
			dispatch(getAssetRefinements(data));
		}
	}, [isGeneratingAssets, generatedAssets, creatives]);

	const selectedVariationsLength = Object.values(selectedVariations).reduce((prev, current) => {
		const currentVariationsLength = current?.variations?.length || 0;
		return currentVariationsLength + prev;
	}, 0);
	const pluralizedVersion = pluralize("Version", selectedVariationsLength);


	useEffect(() => {
		if(!isGeneratingAssets && isEmpty(generatedAssets) || asyncErrors?.versionNamePrefix) {
			onBack();
		}
	}, [isGeneratingAssets, generatedAssets, onBack, asyncErrors]);

	function renderContent(){
		return (
			<div className="blade-content-container">
				<div className="main-content">
					<div className="blade-toolbar">
						<div onClick={onBack}>Current Versions</div>
						<div
							className={showSelected ? "" : "active"}
							onClick={() => setShowSelected(false)}
						>
							Generated{variations.length ? ` ${variations.length}` : ""} Versions
						</div>
						{isAIDevEnabled && (
							<div
								className={showSelected ? "active" : ""}
								onClick={() => setShowSelected(true)}
							>
								Selected Versions {numSelected ? `(${numSelected})` : ""}
							</div>
						)}
					</div>
					<Field
						name="selectedVariations"
						component={GeneratedPreviews}
						libraryId={libraryId}
						validate={[requireSelectedVersions]}
						showSelected={showSelected}
					/>
				</div>
				<div className="panel">
					<AssetsPanel
						onBack={onBack}
						libraryId={libraryId}
						isGenerating={true}
					/>
				</div>
			</div>
		);
	}

	function renderFooter(){
		return (
			<>
				<Button
					theme="default"
					className="cypress-gen-ai-cancel-button"
					onClick={onClose}
				>
					Cancel
				</Button>
				<Button
					type="submit"
					theme="green"
					className="cypress-gen-ai-submit-button"
					onClick={handleSubmit}
					disabled={!valid || submitting}
				>
					Add {pluralizedVersion}{selectedVariationsLength ? ` (${selectedVariationsLength})` : ""} to Version Libraries
				</Button>
			</>
		);
	}

	const bladeProps = {
		title: "Generate Versions for Concept",
		className: `generate-versions-concept-blade ${devClass}`,
		renderContent,
		renderFooter,
		allowFullScreen: false,
		onClose,
	};

	return (
		<form id={AI_GENERATE_VERSIONS_FORM} onSubmit={handleSubmit}>
			<Modal {...bladeProps} />
		</form>
	);
}

GeneratedVersionsBlade.displayName = "GeneratedVersionsBlade";
